import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { UsuarioService } from '../../../../services/usuario.service';
import { AuthService } from '../../../../services/auth.service';
import tippy from 'tippy.js';
import { Usuario } from '../../../../interfaces/usuario/usuario.interface';

@Component({
  selector: 'app-meus-dados',
  templateUrl: './meus-dados.component.html',
  styleUrl: './meus-dados.component.css',
  standalone: false,
})
export class MeusDadosComponent implements OnInit {
  dadosPessoaisForm: FormGroup;
  senhaForm: FormGroup;
  salvandoDados = false;
  alterandoSenha = false;
  emailOriginal: string = '';
  emailInvalido: boolean = false;
  validandoEmail: boolean = false;
  mostrarSenhaAtual = false;
  mostrarNovaSenha = false;
  mostrarConfirmarSenha = false;
  dadosUsuario!: Usuario;

  private regrasHtml = `
    <div class="senha-rules">
      <p class="mb-2">Sua senha deve conter:</p>
      <div class="regra-senha">No mínimo 6 caracteres</div>
      <div class="regra-senha">Ao menos uma letra maiúscula</div>
      <div class="regra-senha">Ao menos uma letra minúscula</div>
      <div class="regra-senha">Ao menos um número</div>
      <div class="regra-senha">Ao menos um caractere especial (!@#$%...)</div>
    </div>
  `;

  @ViewChild('senhaInfo') senhaInfo!: ElementRef;

  constructor(
    private fb: FormBuilder,
    private usuarioService: UsuarioService,
    private toastr: ToastrService,
    private authService: AuthService
  ) {
    this.dadosPessoaisForm = this.fb.group({
      idUsuario: [0],
      email: ['', [Validators.required, Validators.email]],
      login: ['', [Validators.required, Validators.minLength(3)]],
      nome: ['', [Validators.required, Validators.minLength(3)]],
    });

    this.senhaForm = this.fb.group(
      {
        senhaAtual: ['', [Validators.required]],
        novaSenha: [
          '',
          [
            Validators.required,
            Validators.minLength(6),
            Validators.pattern(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/
            ),
          ],
        ],
        confirmarSenha: ['', [Validators.required]],
      },
      {
        validators: [
          this.validarSenhasIguais,
          this.validarSenhaDiferente.bind(this),
        ],
      }
    );
  }

  ngOnInit(): void {
    this.carregarDadosUsuario();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.inicializarTooltips();
    }, 300);
  }

  toggleVisibilidadeSenha(campo: string): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    switch (campo) {
      case 'atual':
        this.mostrarSenhaAtual = !this.mostrarSenhaAtual;
        break;
      case 'nova':
        this.mostrarNovaSenha = !this.mostrarNovaSenha;
        break;
      case 'confirmar':
        this.mostrarConfirmarSenha = !this.mostrarConfirmarSenha;
        break;
    }
  }

  inicializarTooltips(): void {
    if (this.senhaInfo?.nativeElement) {
      tippy(this.senhaInfo.nativeElement, {
        content: this.regrasHtml,
        allowHTML: true,
        placement: 'bottom',
        arrow: true,
        theme: 'dark',
        maxWidth: 320,
        interactive: true,
        appendTo: document.body,
        zIndex: 9999,
      });
    }
  }

  onInputEmail(event: any): void {
    const email = event.target.value;
    this.dadosPessoaisForm.patchValue({ login: email });
  }

  async carregarDadosUsuario(): Promise<void> {
    try {
      this.dadosUsuario = await this.usuarioService.obterDadosUsuarioLogado();
      this.dadosPessoaisForm.patchValue({
        idUsuario: this.dadosUsuario.idUsuario,
        email: this.dadosUsuario.email,
        login: this.dadosUsuario.login,
        nome: this.dadosUsuario.nome,
      });

      this.emailOriginal = this.dadosUsuario.email;
    } catch (error) {
      console.error('Erro ao carregar dados do usuário:', error);
      this.toastr.error(
        'Não foi possível carregar seus dados. Tente novamente mais tarde.'
      );
    }
  }

  validarSenhasIguais(control: AbstractControl): ValidationErrors | null {
    const novaSenha = control.get('novaSenha')?.value;
    const confirmarSenha = control.get('confirmarSenha')?.value;

    if (novaSenha && confirmarSenha && novaSenha !== confirmarSenha) {
      return { senhasNaoConferem: true };
    }

    return null;
  }

  async atualizarDadosPessoais(): Promise<void> {
    if (!this.validarFormularioDadosPessoais()) {
      return;
    }

    try {
      this.salvandoDados = true;
      let dados = this.dadosPessoaisForm.getRawValue();
      this.dadosUsuario.login = dados.login;
      this.dadosUsuario.email = dados.email;
      this.dadosUsuario.nome = dados.nome;
      this.dadosUsuario.senha = '';

      await this.usuarioService.salvarUsuario(this.dadosUsuario);

      this.toastr.success('Seus dados foram atualizados com sucesso!');
    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
      this.toastr.error(
        'Não foi possível atualizar seus dados. Tente novamente mais tarde.'
      );
    } finally {
      this.salvandoDados = false;
    }
  }

  async atualizarSenha(): Promise<void> {
    if (!this.validarFormularioSenha()) {
      return;
    }

    try {
      this.alterandoSenha = true;

      await this.usuarioService.alterarSenha(
        this.senhaForm.value.senhaAtual,
        this.senhaForm.value.novaSenha
      );

      this.toastr.success('Sua senha foi atualizada com sucesso!');
      this.senhaForm.reset();
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
    } finally {
      this.alterandoSenha = false;
    }
  }

  private validarFormularioDadosPessoais(): boolean {
    const form = this.dadosPessoaisForm;
    let valido = true;
    const email = form.get('email')?.value;

    if (form.invalid) {
      const controls = form.controls;

      if (controls['email'].invalid) {
        if (controls['email'].errors?.['required']) {
          this.toastr.error('O e-mail é obrigatório');
          valido = false;
        }
      }

      if (controls['login'].invalid) {
        if (controls['login'].errors?.['required']) {
          this.toastr.error('O login é obrigatório');
        } else if (controls['login'].errors?.['minlength']) {
          this.toastr.error('O login deve ter no mínimo 3 caracteres');
        }
        valido = false;
      }

      if (controls['nome'].invalid) {
        if (controls['nome'].errors?.['required']) {
          this.toastr.error('O nome é obrigatório');
        } else if (controls['nome'].errors?.['minlength']) {
          this.toastr.error('O nome deve ter no mínimo 3 caracteres');
        }
        valido = false;
      }
    }

    if (email && !this.emailInvalido) {
      // Verificação básica de formato com regex
      const emailRegex =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

      if (!emailRegex.test(email)) {
        if (!email.includes('@')) {
          this.toastr.error('Email inválido: falta o símbolo @.');
        } else if (!email.includes('.')) {
          this.toastr.error('Email inválido: falta o ponto no domínio.');
        } else if (email.split('@')[1].split('.').pop()?.length < 2) {
          this.toastr.error(
            'Email inválido: domínio incompleto (falta .com, .org, etc).'
          );
        } else if (email.split('@')[0].length < 1) {
          this.toastr.error('Email inválido: nome de usuário em branco.');
        } else {
          this.toastr.error('Formato de e-mail inválido.');
        }
        valido = false;
      } else {
        const partes = email.split('@');
        const nomeUsuario = partes[0];
        const dominio = partes[1].split('.')[0];

        if (nomeUsuario.length < 3) {
          this.toastr.error(
            'Email inválido: o nome de usuário deve ter pelo menos 3 caracteres.'
          );
          valido = false;
        } else if (dominio.length < 2) {
          this.toastr.error(
            'Email inválido: o domínio deve ter pelo menos 2 caracteres.'
          );
          valido = false;
        } else if (/^([a-z])\1*$/.test(dominio) || /^\d+$/.test(dominio)) {
          this.toastr.error(
            'Email inválido: o domínio parece ser temporário ou inválido.'
          );
          valido = false;
        } else if (
          ['temp', 'fake', 'test', 'mail', 'tempmail', 'mailinator'].includes(
            dominio.toLowerCase()
          )
        ) {
          this.toastr.error(
            'Email inválido: por favor, use um email institucional ou pessoal permanente.'
          );
          valido = false;
        }
      }
    }

    if (this.emailInvalido) {
      this.toastr.error(
        'Este e-mail já está em uso. Por favor, escolha outro.'
      );
      valido = false;
    }

    return valido;
  }

  private validarFormularioSenha(): boolean {
    const form = this.senhaForm;
    let valido = true;

    if (form.invalid) {
      const controls = form.controls;

      if (controls['senhaAtual'].invalid) {
        if (controls['senhaAtual'].errors?.['required']) {
          this.toastr.error('A senha atual é obrigatória');
        }
        valido = false;
      }

      if (controls['novaSenha'].invalid) {
        if (controls['novaSenha'].errors?.['required']) {
          this.toastr.error('A nova senha é obrigatória');
        } else if (controls['novaSenha'].errors?.['minlength']) {
          this.toastr.error('A nova senha deve ter no mínimo 6 caracteres');
        } else if (controls['novaSenha'].errors?.['pattern']) {
          this.toastr.error(
            'A senha deve conter letras maiúsculas, minúsculas, números e caracteres especiais'
          );
        }

        valido = false;
      }

      if (controls['confirmarSenha'].invalid) {
        if (controls['confirmarSenha'].errors?.['required']) {
          this.toastr.error('A confirmação de senha é obrigatória');
        }
        valido = false;
      }

      if (form.errors?.['senhasNaoConferem']) {
        this.toastr.error('As senhas não conferem');
        valido = false;
      }

      if (form.errors?.['senhaMesmaAtual']) {
        this.toastr.error('A nova senha não pode ser igual à senha atual');
        valido = false;
      }
    }

    return valido;
  }

  validarSenhaDiferente(control: AbstractControl): ValidationErrors | null {
    const senhaAtual = control.get('senhaAtual')?.value;
    const novaSenha = control.get('novaSenha')?.value;

    if (senhaAtual && novaSenha && senhaAtual === novaSenha) {
      control.get('novaSenha')?.setErrors({
        ...control.get('novaSenha')?.errors,
        senhaMesmaAtual: true,
      });

      return { senhaMesmaAtual: true };
    }

    if (control.get('novaSenha')?.errors?.['senhaMesmaAtual']) {
      const errors = { ...control.get('novaSenha')?.errors };
      delete errors['senhaMesmaAtual'];

      control
        .get('novaSenha')
        ?.setErrors(Object.keys(errors).length > 0 ? errors : null);
    }

    return null;
  }
}
